import { useTranslation } from "gatsby-plugin-react-i18next";
import { ReactNode, forwardRef, useEffect } from "react";

import { CloseIcon, ErrorIcon, SuccessIcon } from "@theme/assets/icons";
import {
  Box,
  Flex,
  IconButton,
  Paragraph,
  ThemeUIProvider,
  ThemeUIStyleObject,
  getThemeValue,
} from "@theme/components";
import { useToast } from "@theme/context/ToastContext";
import { MsTheme } from "@theme/themes";
import { getTheme } from "@theme/utils";

export interface AlertProps {
  id?: number;
  variant: "error" | "success" | "disclaimer";
  title: string | ReactNode;
  message?: string | ReactNode;
  theme?: MsTheme;
  location?: "inline" | "toast";
  autoClose?: number;
  translationToken?: string;
  className?: string;
  sx?: ThemeUIStyleObject;
  "data-testid"?: string;
  "aria-live"?: "assertive" | "polite";
}

export const Alert = forwardRef<HTMLParagraphElement, AlertProps>(function Alert(
  {
    id,
    variant,
    theme = "LIGHT",
    title,
    message,
    location = "inline",
    autoClose = 3000,
    translationToken,
    className,
    sx,
    "data-testid": dataTestId,
    "aria-live": ariaLive,
  },
  ref,
) {
  const { t } = useTranslation("alert");
  const { removeToast } = useToast();

  useEffect(() => {
    if (!id) return;
    const timer = setTimeout(() => {
      removeToast(id);
    }, autoClose);

    return () => clearTimeout(timer);
  }, [autoClose, id, removeToast]);

  const alertTitle =
    typeof title === "string" && translationToken
      ? t(translationToken, { defaultValue: title })
      : title;

  const alertMessage =
    typeof message === "string" && translationToken
      ? t(translationToken, { defaultValue: message })
      : message;

  const alertTheme = getTheme(theme);
  return (
    <ThemeUIProvider theme={alertTheme}>
      <Box
        className={className}
        role="alert"
        aria-live={ariaLive}
        sx={{
          width: "full",
          bg:
            variant === "success"
              ? "backgroundSuccess"
              : variant === "error"
              ? "backgroundError"
              : "backgroundLevel2",
          py: "s200",
          pl: "s200",
          pr: location === "toast" ? 0 : "s400",
          borderRadius: "xs",
          ...(location === "toast"
            ? {
                boxShadow: "alert",
                zIndex: 99999999,
                maxWidth: 300,
              }
            : {
                // Inline alert only has a border
                borderColor:
                  variant === "success"
                    ? "borderSuccess"
                    : variant === "error"
                    ? "borderError"
                    : "borderLevel2",
                borderStyle: "solid",
                borderWidth: 1,
              }),
          ...sx,
        }}
      >
        <Flex
          sx={{
            borderLeft: (t) =>
              variant === "success"
                ? `4px solid ${getThemeValue(t, "colors.borderSuccess")}`
                : variant === "error"
                ? `4px solid ${getThemeValue(t, "colors.borderError")}`
                : `4px solid ${getThemeValue(t, "colors.borderLevel2")}`,
            pl: "s400",
            py: "s100",
            alignItems: "flex-start",
          }}
        >
          <Flex
            sx={{
              mt: "s50",
              minWidth: 20,
              width: 20,
              height: 20,
              alignItems: "center",
              justifyContent: "center",
              color:
                variant === "success"
                  ? "textSuccess"
                  : variant === "error"
                  ? "textError"
                  : "textLevel3",
            }}
          >
            {variant === "success" && <SuccessIcon />}
            {(variant === "error" || variant === "disclaimer") && <ErrorIcon />}
          </Flex>

          <Box
            sx={{
              pl: "s300",
              textAlign: "left",
              color: "textPrimary",
            }}
          >
            <Paragraph variant="sansBody01Medium" ref={ref} tabIndex={-1} data-testid={dataTestId}>
              {alertTitle}
            </Paragraph>
            {alertMessage && (
              <Paragraph
                ref={ref}
                tabIndex={-1}
                data-testid={dataTestId}
                variant="sansBody01Regular"
              >
                {alertMessage}
              </Paragraph>
            )}
          </Box>
          {location === "toast" && (
            <IconButton
              onClick={() => id && removeToast(id)}
              aria-label="Close"
              variant="iconSm"
              sx={{
                flex: "0 0 28px",
                alignSelf: "start",
                ml: "auto",
                mr: "s100",
                transform: (t) => `translateY(-${getThemeValue(t, "space.s100")})`,
                svg: { color: "textPrimary" },
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Flex>
      </Box>
    </ThemeUIProvider>
  );
});
